import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  CHAT_CLAIM_403_MAX_RETRIES,
  CHAT_CLAIM_403_RETRY_DELAY_MS,
  isChatClaim403RetryableError,
  useGetChat,
  type GetChatResult,
} from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { getChat } from 'tripPlanner/api'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { mergeConsecutiveTextParts } from 'tripPlanner/utils/messageParts'
import { combineMessages } from 'tripPlanner/utils/messageDeduplication'
import { delay } from 'tripPlanner/utils/networkError'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import type { TripPlannerChatMessage } from 'tripPlanner/api/types'

// Message type from API
type ApiChatMessage = Omit<TripPlannerChatMessage, 'parts'> & {
  parts?: Array<MessagePart>
}

// Return type for invalidateChat - allows callers to decide overlay clearing
export interface InvalidateChatResult {
  // IDs of messages that exist AND have content (not empty placeholders)
  messagesWithContent: Set<string>
  skipped: boolean
}

const mapApiMessagesToItems = (messages: Array<ApiChatMessage>, oaiConversationId?: string): Array<MessageItem> => {
  return messages.map((message) => {
    const role = message.role.toLowerCase() as 'user' | 'assistant'

    if (role === 'assistant') {
      const parts: Array<MessagePart> = (message.parts && message.parts.length > 0)
        ? message.parts
        : [{ type: 'text', content: message.content }]

      return {
        id: message.id,
        role,
        parts: mergeConsecutiveTextParts(parts),
        // Use rawContent from API if available, otherwise fall back to content
        rawContent: message.rawContent || message.content,
        oaiConversationId, // From chat level
        messageType: message.type,
      }
    }

    return {
      id: message.id,
      role,
      parts: [{ type: 'text', content: message.content }],
      rawContent: message.content,
      messageType: message.type,
    }
  })
}

interface UseChatMessagesArgs {
  chatId: number | undefined
  isDevMode?: boolean
}

interface UseChatMessagesReturn {
  // Combined messages (server + overlay)
  messages: Array<MessageItem>
  filteredMessages: Array<MessageItem>

  // Streaming overlay state
  pendingUserMessage: MessageItem | undefined
  streamingMessage: MessageItem | undefined
  setPendingUserMessage: (message: MessageItem | undefined) => void
  setStreamingMessage: (message: MessageItem | undefined) => void
  updateStreamingMessageParts: (parts: Array<MessagePart>) => void
  updateStreamingMessageMetadata: (metadata: { oaiConversationId?: string; rawContent?: string }) => void

  // React Query controls - returns messages with actual content (not empty placeholders)
  invalidateChat: (forChatId?: number) => Promise<InvalidateChatResult>
  isQueryLoading: boolean
  isChatError: boolean
  chatError: unknown

  // Feedback map (separate from messages)
  userFeedbackMap: Record<string, 'UP' | 'DOWN'>
  setUserFeedbackMap: React.Dispatch<React.SetStateAction<Record<string, 'UP' | 'DOWN'>>>

  // Reset
  resetMessages: () => void
}

export function useChatMessages({ chatId, isDevMode = false }: UseChatMessagesArgs): UseChatMessagesReturn {
  const queryClient = useQueryClient()

  // React Query - source of truth for persisted messages
  // Retry on 403 to tolerate the race between POST /chats/claim (ownership
  // transfer) and this query firing with the newly-authenticated cookie.
  const { data: chatData, isLoading: isQueryLoading, isError: isChatError, error: chatError } = useGetChat(chatId, {
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  // Streaming overlay state (local only)
  const [pendingUserMessage, setPendingUserMessage] = useState<MessageItem | undefined>(undefined)
  const [streamingMessage, setStreamingMessage] = useState<MessageItem | undefined>(undefined)
  const [userFeedbackMap, setUserFeedbackMap] = useState<Record<string, 'UP' | 'DOWN'>>({})

  // Ref to track current chatId for invalidateChat (avoids stale closure)
  const chatIdRef = useRef<number | undefined>(chatId)
  chatIdRef.current = chatId

  // Track the chatId to reset overlay on chat switch
  const previousChatIdRef = useRef<number | undefined>(chatId)

  // Clear overlay when switching between EXISTING chats (not when creating a new chat)
  // When previousChatId is undefined and chatId becomes defined, that's a new chat creation - keep overlay
  // When both are defined and different, that's switching chats - clear overlay
  // Also force a refetch to get fresh data (handles case where streaming completed while on another chat)
  useEffect(() => {
    const previousChatId = previousChatIdRef.current
    const isSwitchingBetweenExistingChats = previousChatId !== undefined && chatId !== undefined && previousChatId !== chatId

    if (isSwitchingBetweenExistingChats) {
      setPendingUserMessage(undefined)
      setStreamingMessage(undefined)
      // Force refetch to get any messages that completed while we were away
      queryClient.invalidateQueries({ queryKey: ChatKeys.detail(chatId) })
    }
    previousChatIdRef.current = chatId
  }, [chatId, queryClient])

  const serverMessages = useMemo(() => {
    if (!chatData?.messages) {
      return []
    }
    return mapApiMessagesToItems(chatData.messages as Array<ApiChatMessage>, chatData.oaiConversationId)
  }, [chatData])

  useEffect(() => {
    if (!chatData?.messages) return
    const feedbackMap = Object.fromEntries(
      chatData.messages
        .filter((m) => m.feedback?.reaction)
        .map((m) => [m.id, m.feedback!.reaction]),
    )
    setUserFeedbackMap(feedbackMap)
  }, [chatData?.messages])

  // Client-generated UUIDs enable simple ID-based deduplication between local and server
  const messages = useMemo(() => {
    return combineMessages(serverMessages, pendingUserMessage, streamingMessage)
  }, [serverMessages, pendingUserMessage, streamingMessage])

  const filteredMessages = useMemo(
    () => (isDevMode
      ? messages
      : messages.filter(m =>
          m.messageType === 'message' ||
          m.messageType === 'error' ||
          m.messageType === undefined,
        )),
    [messages, isDevMode],
  )

  const updateStreamingMessageParts = useCallback((parts: Array<MessagePart>) => {
    setStreamingMessage(prev => prev ? { ...prev, parts } : undefined)
  }, [])

  const updateStreamingMessageMetadata = useCallback((metadata: { oaiConversationId?: string; rawContent?: string }) => {
    setStreamingMessage(prev => prev ? { ...prev, ...metadata } : undefined)
  }, [])

  // Invalidate React Query cache and return messages with actual content
  // Callers use this to decide whether to clear overlays
  // This avoids stale closure issues - callers have the message IDs as local variables
  // IMPORTANT: Only returns messages that have content - empty placeholders are excluded
  const invalidateChat = useCallback(async(forChatId?: number): Promise<InvalidateChatResult> => {
    const currentChatId = chatIdRef.current

    if (forChatId !== undefined && forChatId !== currentChatId) {
      return { messagesWithContent: new Set(), skipped: true }
    }

    if (!currentChatId) {
      return { messagesWithContent: new Set(), skipped: true }
    }

    queryClient.invalidateQueries({ queryKey: ChatKeys.detail(currentChatId) })

    try {
      let freshData: GetChatResult | undefined

      for (let attempt = 0; attempt <= CHAT_CLAIM_403_MAX_RETRIES; attempt += 1) {
        try {
          freshData = await queryClient.fetchQuery<GetChatResult>({
            queryKey: ChatKeys.detail(currentChatId),
            queryFn: () => getChat(currentChatId),
          })
          break
        } catch(error) {
          if (!isChatClaim403RetryableError(error) || attempt === CHAT_CLAIM_403_MAX_RETRIES) {
            throw error
          }

          await delay(CHAT_CLAIM_403_RETRY_DELAY_MS)
        }
      }

      // Only include messages that have actual content (not empty placeholders)
      // This prevents clearing the overlay when the server has a placeholder but hasn't saved content yet
      const messagesWithContent = new Set(
        (freshData?.messages ?? [])
          .filter(m => m.content && m.content.trim().length > 0)
          .map(m => m.id),
      )

      return { messagesWithContent, skipped: false }
    } catch {
      // On fetch error, return empty set - caller will keep overlays visible
      // Next refetch (e.g., window focus) will sync properly
      return { messagesWithContent: new Set(), skipped: false }
    }
  }, [queryClient])

  const resetMessages = useCallback(() => {
    setPendingUserMessage(undefined)
    setStreamingMessage(undefined)
    setUserFeedbackMap({})
  }, [])

  return {
    messages,
    filteredMessages,
    pendingUserMessage,
    streamingMessage,
    setPendingUserMessage,
    setStreamingMessage,
    updateStreamingMessageParts,
    updateStreamingMessageMetadata,
    invalidateChat,
    isQueryLoading,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
    resetMessages,
  }
}
